import type { DocDisplayConfig } from '../components/ai-chat-chips';
import type {
  AiDocSourcePillItem,
  AiMailSourcePillItem,
  AiSourcePillItem,
  AiWebSourcePillItem,
} from '../components/ai-tools/doc-search-source';
import type {
  AssistantSourceItem,
  AssistantSourceManifest,
} from './assistant-source-manifest';

export function assistantSourceManifestToPillItems(
  manifest: AssistantSourceManifest,
  docDisplayService?: DocDisplayConfig
): AiSourcePillItem[] {
  return manifest.items.flatMap(item =>
    assistantSourceItemToPillItem(item, docDisplayService)
  );
}

export function assistantSourceManifestToDocPillItems(
  manifest: AssistantSourceManifest,
  docDisplayService?: DocDisplayConfig
): AiDocSourcePillItem[] {
  return assistantSourceManifestToPillItems(manifest, docDisplayService).filter(
    (item): item is AiDocSourcePillItem =>
      item.sourceKind !== 'mail' && item.sourceKind !== 'web'
  );
}

export function assistantSourceManifestToMailPillItems(
  manifest: AssistantSourceManifest
): AiMailSourcePillItem[] {
  return assistantSourceManifestToPillItems(manifest).filter(
    (item): item is AiMailSourcePillItem => item.sourceKind === 'mail'
  );
}

function assistantSourceItemToPillItem(
  item: AssistantSourceItem,
  docDisplayService?: DocDisplayConfig
): AiSourcePillItem[] {
  switch (item.kind) {
    case 'doc-search':
      return docSearchSourceItemToPillItem(item, docDisplayService);
    case 'created-doc':
      return createdDocSourceItemToPillItem(item, docDisplayService);
    case 'mail':
      return mailSourceItemToPillItem(item);
    case 'web':
      return webSourceItemToPillItem(item);
    default:
      return [];
  }
}

function webSourceItemToPillItem(
  item: AssistantSourceItem
): AiWebSourcePillItem[] {
  const url = stringRef(item, 'url');
  if (!url) return [];
  return [
    {
      sourceKind: 'web',
      title: item.display.title || item.label || url,
      url,
      snippet: item.display.preview ?? undefined,
    },
  ];
}

function docSearchSourceItemToPillItem(
  item: AssistantSourceItem,
  docDisplayService?: DocDisplayConfig
): AiDocSourcePillItem[] {
  const docId = stringRef(item, 'docId');
  if (!docId) return [];

  const sourceKind = stringRef(item, 'sourceKind') as
    | AiDocSourcePillItem['sourceKind']
    | undefined;
  const sourceType = stringSourceField(item, 'sourceType');
  const docTitle = stringSourceField(item, 'docTitle');
  const attachmentName = stringSourceField(item, 'attachmentName');
  return [
    {
      citationId: stringRef(item, 'citationId'),
      docId,
      blockId: stringRef(item, 'blockId'),
      sourceKind,
      sourceType,
      elementId: stringSourceField(item, 'elementId'),
      parentBlockId: stringSourceField(item, 'parentBlockId'),
      parentFlavour: stringSourceField(item, 'parentFlavour'),
      noteBlockId: stringSourceField(item, 'noteBlockId'),
      displayMode: stringSourceField(item, 'displayMode'),
      docTitle,
      attachmentName,
      blobKey: stringSourceField(item, 'blobKey'),
      databaseId: stringSourceField(item, 'databaseId'),
      databaseRowId: stringSourceField(item, 'databaseRowId'),
      databaseColumnId: stringSourceField(item, 'databaseColumnId'),
      databaseTitle: stringSourceField(item, 'databaseTitle'),
      databaseRowLabel: stringSourceField(item, 'databaseRowLabel'),
      databaseColumnName: stringSourceField(item, 'databaseColumnName'),
      visibility: stringSourceField(item, 'visibility') as
        | AiDocSourcePillItem['visibility']
        | undefined,
      technicalSourceKind: stringSourceField(item, 'technicalSourceKind'),
      pageIndex: numberRef(item, 'pageIndex'),
      query: stringRef(item, 'query') || stringPayload(item, 'query'),
      snippet: item.display.preview ?? undefined,
      rects: Array.isArray(item.payload?.rects)
        ? (item.payload.rects as AiDocSourcePillItem['rects'])
        : undefined,
      title: docDisplayService?.getTitle(docId) || item.display.title,
    },
  ];
}

function stringSourceField(
  item: AssistantSourceItem,
  key: string
): string | undefined {
  return stringRef(item, key) || stringPayload(item, key);
}

function createdDocSourceItemToPillItem(
  item: AssistantSourceItem,
  docDisplayService?: DocDisplayConfig
): AiDocSourcePillItem[] {
  const docId = stringRef(item, 'docId');
  if (!docId) return [];

  const title = docDisplayService?.getTitle(docId) || item.display.title;
  return [
    {
      docId,
      title,
      docTitle: title,
      sourceKind: 'doc',
    },
  ];
}

function mailSourceItemToPillItem(
  item: AssistantSourceItem
): AiMailSourcePillItem[] {
  return [
    {
      sourceKind: 'mail',
      title: item.display.title,
      messageId: stringRef(item, 'messageId'),
      from: stringRef(item, 'from'),
      receivedAt: stringRef(item, 'receivedAt'),
      snippet: item.display.preview ?? undefined,
    },
  ];
}

function stringRef(item: AssistantSourceItem, key: string): string | undefined {
  const value = item.refs[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function numberRef(item: AssistantSourceItem, key: string): number | undefined {
  const value = item.refs[key];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function stringPayload(
  item: AssistantSourceItem,
  key: string
): string | undefined {
  const value = item.payload?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}
